'use client';

import _slicedToArray from "/opt/atlassian/pipelines/agent/build/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import * as React from 'react';
import { useSyncExternalStore } from './useSyncExternalStore.esm.js';
import { notifyManager } from '@tanstack/query-core';
import { useQueryErrorResetBoundary } from './QueryErrorResetBoundary.esm.js';
import { useQueryClient } from './QueryClientProvider.esm.js';
import { useIsRestoring } from './isRestoring.esm.js';
import { ensurePreventErrorBoundaryRetry, useClearResetErrorBoundary, getHasError } from './errorBoundaryUtils.esm.js';
import { ensureStaleTime, shouldSuspend, fetchOptimistic } from './suspense.esm.js';
function useBaseQuery(options, Observer) {
  var queryClient = useQueryClient({
    context: options.context
  });
  var isRestoring = useIsRestoring();
  var errorResetBoundary = useQueryErrorResetBoundary();
  var defaultedOptions = queryClient.defaultQueryOptions(options); // Make sure results are optimistically set in fetching state before subscribing or updating options

  defaultedOptions._optimisticResults = isRestoring ? 'isRestoring' : 'optimistic'; // Include callbacks in batch renders

  if (defaultedOptions.onError) {
    defaultedOptions.onError = notifyManager.batchCalls(defaultedOptions.onError);
  }
  if (defaultedOptions.onSuccess) {
    defaultedOptions.onSuccess = notifyManager.batchCalls(defaultedOptions.onSuccess);
  }
  if (defaultedOptions.onSettled) {
    defaultedOptions.onSettled = notifyManager.batchCalls(defaultedOptions.onSettled);
  }
  ensureStaleTime(defaultedOptions);
  ensurePreventErrorBoundaryRetry(defaultedOptions, errorResetBoundary);
  useClearResetErrorBoundary(errorResetBoundary);
  var _React$useState = React.useState(function () {
      return new Observer(queryClient, defaultedOptions);
    }),
    _React$useState2 = _slicedToArray(_React$useState, 1),
    observer = _React$useState2[0];
  var result = observer.getOptimisticResult(defaultedOptions);
  useSyncExternalStore(React.useCallback(function (onStoreChange) {
    return isRestoring ? function () {
      return undefined;
    } : observer.subscribe(notifyManager.batchCalls(onStoreChange));
  }, [observer, isRestoring]), function () {
    return observer.getCurrentResult();
  }, function () {
    return observer.getCurrentResult();
  });
  React.useEffect(function () {
    // Do not notify on updates because of changes in the options because
    // these changes should already be reflected in the optimistic result.
    observer.setOptions(defaultedOptions, {
      listeners: false
    });
  }, [defaultedOptions, observer]); // Handle suspense

  if (shouldSuspend(defaultedOptions, result, isRestoring)) {
    throw fetchOptimistic(defaultedOptions, observer, errorResetBoundary);
  } // Handle error boundary

  if (getHasError({
    result: result,
    errorResetBoundary: errorResetBoundary,
    useErrorBoundary: defaultedOptions.useErrorBoundary,
    query: observer.getCurrentQuery()
  })) {
    throw result.error;
  } // Handle result property usage tracking

  return !defaultedOptions.notifyOnChangeProps ? observer.trackResult(result) : result;
}
export { useBaseQuery };